import type {
  DuplicateGroup,
  TranslationRow,
} from "@/v2-backoffice-client/translation/translation.types"

function signature(row: TranslationRow): string {
  return (row.enName || row.nbName || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "")
}

function editDistance(a: string, b: string): number {
  const dp = Array.from({ length: b.length + 1 }, (_, i) => i)
  for (let i = 1; i <= a.length; i++) {
    let prev = dp[0]
    dp[0] = i
    for (let j = 1; j <= b.length; j++) {
      const tmp = dp[j]
      dp[j] =
        a[i - 1] === b[j - 1] ? prev : 1 + Math.min(prev, dp[j], dp[j - 1])
      prev = tmp
    }
  }
  return dp[b.length]
}

function isNear(a: string, b: string): boolean {
  if (!a || !b) return false
  const dist = editDistance(a, b)
  return dist > 0 && dist <= 2 && dist / Math.max(a.length, b.length) <= 0.34
}

export function toDuplicateGroup(
  key: string,
  rows: TranslationRow[],
  source: string,
): DuplicateGroup {
  return {
    key: `${source}:${key}`,
    name: rows[0].enName || rows[0].nbName,
    count: rows.length,
    // Newline-joined; the Codes column renders it whitespace-pre-line so
    // every code sits on its own row.
    codesText: rows.map((row) => row.code).join("\n"),
    rows,
    source,
  }
}

// Exact + fuzzy (edit-distance) grouping over the catalog. Catches identical
// and spelling-variant labels; semantic synonyms come from the Opus scan.
// O(n^2) only within 3-char-prefix buckets, so it stays fast at catalog
// scale — but it is still the tab's heaviest client-side computation, which
// is why TranslationDuplicatesView computes it only when that view is open.
export function buildFuzzyGroups(rows: TranslationRow[]): DuplicateGroup[] {
  const bySig = new Map<string, TranslationRow[]>()
  for (const row of rows) {
    const sig = signature(row)
    if (!sig) continue
    const list = bySig.get(sig)
    if (list) list.push(row)
    else bySig.set(sig, [row])
  }
  const sigs = [...bySig.keys()]
  const parent = new Map<string, string>(sigs.map((s) => [s, s]))
  const find = (s: string): string => {
    let root = s
    while (parent.get(root) !== root) root = parent.get(root)!
    return root
  }
  const buckets = new Map<string, string[]>()
  for (const sig of sigs) {
    const key = sig.slice(0, 3)
    const list = buckets.get(key)
    if (list) list.push(sig)
    else buckets.set(key, [sig])
  }
  for (const list of buckets.values()) {
    for (let i = 0; i < list.length; i++) {
      for (let j = i + 1; j < list.length; j++) {
        if (isNear(list[i], list[j])) parent.set(find(list[i]), find(list[j]))
      }
    }
  }
  const clusters = new Map<string, TranslationRow[]>()
  for (const sig of sigs) {
    const root = find(sig)
    const existing = clusters.get(root)
    if (existing) existing.push(...bySig.get(sig)!)
    else clusters.set(root, [...bySig.get(sig)!])
  }
  return [...clusters.entries()]
    .filter(([, groupRows]) => groupRows.length > 1)
    .map(([root, groupRows]) => toDuplicateGroup(root, groupRows, "fuzzy"))
}
